import { create } from 'zustand';
import { researchApi, type ResearchJob, type ResearchRequest } from '@/services/api';

interface ResearchState {
  jobs: ResearchJob[];
  activeJob: ResearchJob | null;
  loading: boolean;
  error: string | null;

  startResearch: (req: ResearchRequest) => Promise<string>;
  pollStatus: (id: string) => Promise<void>;
  setActiveJob: (job: ResearchJob | null) => void;
  clearError: () => void;
}

export const useResearchStore = create<ResearchState>((set, get) => ({
  jobs: [],
  activeJob: null,
  loading: false,
  error: null,

  startResearch: async (req) => {
    set({ loading: true, error: null });
    try {
      const { research_id } = await researchApi.startCustomerAsync(req);
      const newJob: ResearchJob = {
        research_id,
        status: 'pending',
        company_name: req.company_name,
        created_at: new Date().toISOString(),
      };
      set((s) => ({
        jobs: [newJob, ...s.jobs],
        activeJob: newJob,
      }));
      return research_id;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to start research';
      set({ error: msg });
      throw e;
    } finally {
      set({ loading: false });
    }
  },

  pollStatus: async (id) => {
    try {
      const job = await researchApi.getStatus(id);
      set((s) => ({
        jobs: s.jobs.map((j) => (j.research_id === id ? job : j)),
        activeJob:
          s.activeJob?.research_id === id ? job : s.activeJob,
      }));
    } catch (e) {
      set({
        error: e instanceof Error ? e.message : 'Failed to poll status',
      });
    }
  },

  setActiveJob: (job) => set({ activeJob: job }),
  clearError: () => set({ error: null }),
}));
